(() => {
  if (window.__wwHdrTone) return;
  window.__wwHdrTone = true;

  const header = document.getElementById('header-component');
  const main = document.getElementById('MainContent');
  if (!header || !main) return;

  header.dataset.wwChameleon = 'true';

  const scroller = document.querySelector('.page-wrapper');
  const firstShopifySection = main.querySelector(':scope > .shopify-section:first-child');
  const firstSurface = firstShopifySection && (
    firstShopifySection.querySelector('.ww-hx__stage, .ww-hub-hero, .ww-pdp-opening, .ww-article-band, .ww-calculator-hero, .wwal-hero') ||
    firstShopifySection.querySelector(':scope > .ww-page-section, :scope > .ww-section, :scope > .section') ||
    firstShopifySection.querySelector(':scope > section, :scope > article > header, :scope > article')
  );

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseColor = (value) => {
    if (!value || value === 'transparent') return null;

    if (value[0] === '#') {
      const hex = value.slice(1);
      if (hex.length === 3) return hex.split('').map((part) => parseInt(part + part, 16));
      if (hex.length >= 6) return [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16));
      return null;
    }

    const match = value.match(/rgba?\(([^)]+)\)/i);
    if (!match) return null;
    const parts = match[1].replace(/\//g, ' ').split(/[\s,]+/).filter(Boolean);
    const alpha = parts[3] === undefined ? 1 : Number(parts[3]);
    if (Number.isNaN(alpha) || alpha < .08) return null;
    return parts.slice(0, 3).map((part) => clamp(Number.parseFloat(part), 0, 255));
  };

  const gradientColor = (style, element, y) => {
    if (!style.backgroundImage || style.backgroundImage === 'none') return null;
    const tokens = style.backgroundImage.match(/#[0-9a-f]{3,8}|rgba?\([^)]+\)/gi) || [];
    const colors = tokens.map(parseColor).filter(Boolean);
    if (!colors.length) return null;
    if (colors.length === 1) return colors[0];

    const rect = element.getBoundingClientRect();
    const progress = rect.height ? clamp((y - rect.top) / rect.height, 0, 1) : 0;
    const scaled = progress * (colors.length - 1);
    const start = Math.min(colors.length - 2, Math.floor(scaled));
    const mix = scaled - start;
    return colors[start].map((channel, index) => channel + (colors[start + 1][index] - channel) * mix);
  };

  /* Keep the approved home/PDP tone vocabulary as the deterministic first
     choice, then fall back to the actual computed background for any section. */
  const knownTone = (element) => {
    if (!element) return null;
    const match = element.closest(
      '.ww-hx__stage, .ww-spot__stage, .ww-pan, .ww3-day, [data-ww-page-tone], .wwal-hero'
    );
    if (!match) return null;

    if (match.matches('.ww-hx__stage')) return [125, 94, 108];
    if (match.matches('.ww-spot__stage')) return [31, 17, 25];
    if (match.matches('.ww-pan')) return parseColor(match.dataset.tone === 'pm' ? '#f6f1f6' : '#fbf3ec');
    if (match.matches('.ww3-day')) return parseColor(match.dataset.bg || '#27161e');
    if (match.matches('.wwal-hero')) return parseColor('#261329');

    const tone = match.dataset.wwPageTone;
    const tones = {
      am: '#efb384', pm: '#5c4657', set: '#84696f',
      'am-soft': '#fff8f1', 'pm-soft': '#fcf9fc', 'set-soft': '#fbf4f0', sage: '#e3e9dc'
    };
    return parseColor(tones[tone]);
  };

  const computedTone = (start, y) => {
    let element = start;
    while (element && element !== document.documentElement) {
      const style = getComputedStyle(element);
      const fromGradient = gradientColor(style, element, y);
      if (fromGradient) return fromGradient;
      const fromColor = parseColor(style.backgroundColor);
      if (fromColor) return fromColor;

      const before = getComputedStyle(element, '::before');
      const fromBefore = gradientColor(before, element, y) || parseColor(before.backgroundColor);
      if (fromBefore) return fromBefore;
      element = element.parentElement;
    }
    return [255, 255, 255];
  };

  const luminance = (rgb) => {
    const linear = rgb.map((channel) => {
      const value = channel / 255;
      return value <= .03928 ? value / 12.92 : Math.pow((value + .055) / 1.055, 2.4);
    });
    return .2126 * linear[0] + .7152 * linear[1] + .0722 * linear[2];
  };

  let frame = 0;
  let lastKey = '';

  const update = () => {
    frame = 0;
    const rect = header.getBoundingClientRect();
    const y = clamp(rect.bottom + 2, 0, window.innerHeight - 1);
    const x = clamp(window.innerWidth / 2, 0, window.innerWidth - 1);
    const beneath = document.elementFromPoint(x, y);
    const rgb = knownTone(beneath) || computedTone(beneath, y);
    const rounded = rgb.map((channel) => Math.round(channel));

    const firstRect = firstSurface?.getBoundingClientRect();
    const scrolled = Math.max(window.scrollY || 0, scroller ? scroller.scrollTop : 0) > 8;
    const pastFirst = scrolled || !firstRect || firstRect.bottom <= rect.bottom + 2;
    const key = `${rounded.join(',')}:${pastFirst}`;
    if (key === lastKey) return;
    lastKey = key;

    header.style.setProperty('--ww-hdr-bg', `rgba(${rounded.join(', ')}, .9)`);
    header.dataset.wwInk = luminance(rounded) < .35 ? 'light' : 'dark';
    header.dataset.wwSurface = pastFirst ? 'true' : 'false';
  };

  const requestUpdate = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
  document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
  scroller?.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  document.addEventListener('shopify:section:load', requestUpdate);
  document.addEventListener('click', () => window.setTimeout(requestUpdate, 50));

  if ('ResizeObserver' in window) new ResizeObserver(requestUpdate).observe(header);
  if ('MutationObserver' in window) {
    new MutationObserver(requestUpdate).observe(main, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-bg', 'data-tone', 'data-ww-page-tone']
    });
  }

  update();
})();
